import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidste led i sletningen: brugeren er kommet hertil fra et link i sin egen
// indbakke. Selskabet forsvinder ikke her — integrationen til Digital Post og
// e-Boks skal pilles ned foerst, ellers ville posten blive ved med at komme
// ind til et selskab der ikke laengere findes.
export default function DeleteCompanyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [selskab, setSelskab] = useState(null);
  const [fejl, setFejl] = useState(null);
  const [henter, setHenter] = useState(true);
  const [sender, setSender] = useState(false);
  const [faerdig, setFaerdig] = useState(false);

  useEffect(() => {
    if (!token) { setFejl('Der mangler et token i linket'); setHenter(false); return; }
    axios.get(`${API}/companies/delete-request/${token}`)
      .then(r => setSelskab(r.data))
      .catch(e => setFejl(e.response?.data?.detail || 'Linket kunne ikke bruges'))
      .finally(() => setHenter(false));
  }, [token]);

  const bekraeft = async () => {
    setSender(true);
    try {
      await axios.post(`${API}/companies/delete-request/${token}/confirm`);
      setFaerdig(true);
    } catch (e) {
      setFejl(e.response?.data?.detail || 'Kunne ikke bekræfte');
    } finally { setSender(false); }
  };

  return (
    <div className="min-h-screen bg-th-body flex items-center justify-center p-4">
      <Card className="bg-th-input border-th-border p-8 max-w-lg w-full space-y-5">
        {henter ? (
          <p className="text-th-text-secondary text-center">Indlæser...</p>
        ) : fejl ? (
          <div className="text-center space-y-4">
            <AlertCircle size={44} className="text-amber-500 mx-auto" />
            <h1 className="text-xl font-heading font-bold text-th-text">Linket kan ikke bruges</h1>
            <p className="text-th-text-secondary text-sm">{fejl}</p>
            <Button onClick={() => navigate('/selskaber')} className="bg-btn hover:bg-btn-hover text-btn-fg">
              Til mine selskaber
            </Button>
          </div>
        ) : faerdig ? (
          <div className="text-center space-y-4">
            <CheckCircle size={44} className="text-green-500 mx-auto" />
            <h1 className="text-xl font-heading font-bold text-th-text">Sletning bekræftet</h1>
            <p className="text-th-text-secondary text-sm">
              Vi har registreret at <strong className="text-th-text">{selskab?.name}</strong> (CVR {selskab?.cvr}) skal slettes.
            </p>
            <p className="text-th-text-secondary text-sm">
              <strong className="text-th-text">Integrationen til Digital Post og e-Boks skal fjernes,
              før sletningen træder helt i kraft.</strong> Vores support sørger for det og bekræfter
              over for dig, når det er sket.
            </p>
            <Button onClick={() => navigate('/selskaber')} className="bg-btn hover:bg-btn-hover text-btn-fg">
              Til mine selskaber
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <Trash2 size={18} className="text-red-500" />
              </div>
              <h1 className="text-xl font-heading font-bold text-th-text">Slet {selskab?.name}?</h1>
            </div>
            <p className="text-th-text-secondary text-sm">
              Du er ved at bekræfte, at <strong className="text-th-text">{selskab?.name}</strong> (CVR {selskab?.cvr})
              skal slettes fra HentPost. Det kan ikke fortrydes — al modtaget post og alle dokumenter
              på selskabet forsvinder med.
            </p>
            <p className="text-th-text-secondary text-sm">
              <strong className="text-th-text">Integrationen til Digital Post og e-Boks skal fjernes,
              før sletningen træder helt i kraft.</strong> Det gør vores support, og du får besked, når
              det er sket. Indtil da kan der stadig komme post ind på selskabet.
            </p>
            <div className="flex gap-3 pt-1">
              <Button onClick={bekraeft} disabled={sender} className="bg-red-600 hover:bg-red-700 text-white">
                {sender ? 'Bekræfter...' : 'Ja, slet selskabet'}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/selskaber')} className="text-th-text-secondary">
                Fortryd
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
